import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { getCompensationMeta, revealCompensation, putCompensation } from '../api/compensationApi';

const MASK = '*********';

/** Same rules as backend: 1–14 digits, optional . and up to 2 decimal places. */
const VALID_AMOUNT = /^\d{1,14}(\.\d{1,2})?$/;

function sanitizeAmountDraft(raw) {
  let v = String(raw).replace(/[^\d.]/g, '');
  const firstDot = v.indexOf('.');
  if (firstDot === -1) {
    return v.slice(0, 14);
  }
  const whole = v.slice(0, firstDot).replace(/\./g, '').slice(0, 14);
  const frac = v.slice(firstDot + 1).replace(/\./g, '').slice(0, 2);
  const endsWithBareDot = v.endsWith('.') && frac.length === 0;
  if (endsWithBareDot) {
    return `${whole}.`;
  }
  if (frac.length > 0) {
    return `${whole || '0'}.${frac}`;
  }
  return whole;
}

export default function CompensationHrSection({ employeeUserId, canEdit }) {
  const qc = useQueryClient();
  const [revealed, setRevealed] = useState(false);
  const [revealedAmount, setRevealedAmount] = useState(null);
  const [draft, setDraft] = useState('');

  const { data: meta, isLoading } = useQuery({
    queryKey: ['compensation-meta', employeeUserId],
    queryFn: () => getCompensationMeta(employeeUserId),
    enabled: !!employeeUserId,
  });

  useEffect(() => {
    setRevealed(false);
    setRevealedAmount(null);
    setDraft('');
  }, [employeeUserId]);

  const revealMutation = useMutation({
    mutationFn: () => revealCompensation(employeeUserId),
    onSuccess: (data) => {
      setRevealedAmount(data?.amount ?? null);
      setRevealed(true);
      if (data?.amount != null && data.amount !== '') {
        setDraft(String(data.amount));
      }
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Could not load compensation'),
  });

  const saveMutation = useMutation({
    mutationFn: (amount) => putCompensation(employeeUserId, amount),
    onSuccess: (_, amount) => {
      toast.success(amount === null || amount === '' ? 'Compensation cleared' : 'Compensation saved');
      qc.invalidateQueries({ queryKey: ['compensation-meta', employeeUserId] });
      setRevealed(false);
      setRevealedAmount(null);
      if (amount === null || amount === '') {
        setDraft('');
      }
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Save failed'),
  });

  const hasCompensation = meta?.has_compensation === true;

  const displayValue = () => {
    if (!hasCompensation) return null;
    if (!revealed) return MASK;
    if (revealedAmount == null || revealedAmount === '') return null;
    return revealedAmount;
  };

  const shownDisplay = displayValue();
  const showEditForm = canEdit && (!hasCompensation || revealed);

  return (
    <div
      style={{
        marginTop: '1.5rem',
        paddingTop: '1.5rem',
        borderTop: '1px solid #e2e8f0',
      }}
    >
      <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
        Compensation (package)
      </h4>

      {isLoading ? (
        <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Loading…</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          {shownDisplay != null && shownDisplay !== '' ? (
            <span
              style={{
                fontFamily: 'ui-monospace, monospace',
                fontSize: '1rem',
                fontWeight: 700,
                letterSpacing: revealed ? 'normal' : '0.12em',
                color: '#0f172a',
                minWidth: 120,
              }}
            >
              {shownDisplay}
            </span>
          ) : null}
          {hasCompensation ? (
            <button
              type="button"
              onClick={() => {
                if (revealed) {
                  setRevealed(false);
                  setRevealedAmount(null);
                  setDraft('');
                } else {
                  revealMutation.mutate();
                }
              }}
              disabled={revealMutation.isPending}
              title={revealed ? 'Hide amount' : 'Reveal amount'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '0.35rem 0.65rem',
                borderRadius: 8,
                border: '1px solid #cbd5e1',
                background: '#fff',
                cursor: revealMutation.isPending ? 'wait' : 'pointer',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#334155',
              }}
            >
              {revealed ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              {revealed ? 'Hide' : 'Reveal'}
            </button>
          ) : null}
        </div>
      )}

      {canEdit && hasCompensation && !revealed ? (
        <p style={{ margin: '0.75rem 0 0', fontSize: '0.72rem', color: '#64748b', lineHeight: 1.4 }}>
          Reveal the amount above to update or clear it (HR only).
        </p>
      ) : null}

      {showEditForm ? (
        <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>
            Update amount (INR per annum)
          </label>
          <input
            type="text"
            inputMode="decimal"
            placeholder="INR p.a., digits only (e.g. 1200000 or 850000.50)"
            value={draft}
            onChange={(e) => setDraft(sanitizeAmountDraft(e.target.value))}
            style={{
              padding: '0.5rem 0.65rem',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: '0.9rem',
            }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={() => {
                const t = draft.trim();
                if (t === '') {
                  saveMutation.mutate(null);
                  return;
                }
                if (!VALID_AMOUNT.test(t)) {
                  toast.error('Use digits only, with at most 2 decimal places (e.g. 850000 or 12500.50).');
                  return;
                }
                saveMutation.mutate(t);
              }}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: 8,
                border: 'none',
                background: '#0f172a',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: saveMutation.isPending ? 'wait' : 'pointer',
              }}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              disabled={saveMutation.isPending || !hasCompensation}
              onClick={() => {
                if (window.confirm('Clear stored compensation for this employee?')) {
                  saveMutation.mutate(null);
                }
              }}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: 8,
                border: '1px solid #cbd5e1',
                background: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: saveMutation.isPending || !hasCompensation ? 'not-allowed' : 'pointer',
                opacity: !hasCompensation ? 0.5 : 1,
              }}
            >
              Clear
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

